'use client';

import { useEffect, useRef } from 'react';
import { usePathname } from 'next/navigation';
import { PROBE_HANDOFF_KEY, PROBE_KEY, resolveProbeExpiry } from './probe-policy';
import type { startNavigationProbe } from './navigation-probe';

/** Hash opt-in never reaches middleware or changes an ISR/cache key. */
export function NavigationProbeClient() {
  const pathname = usePathname();
  const output = useRef<HTMLOutputElement>(null);
  const probe = useRef<ReturnType<typeof startNavigationProbe> | null>(null);
  useEffect(() => {
    let disposed = false;
    let generation = 0;
    let expiryTimer: ReturnType<typeof setTimeout> | undefined;
    const stop = () => {
      generation++;
      clearTimeout(expiryTimer);
      probe.current?.stop();
      probe.current = null;
    };
    const sync = () => {
      stop();
      let saved: string | null = null;
      try {
        saved = sessionStorage.getItem(PROBE_KEY);
      } catch {
        /* Private browsing. */
      }
      const until = resolveProbeExpiry(location.hash, saved, Date.now());
      try {
        if (until) sessionStorage.setItem(PROBE_KEY, String(until));
        else sessionStorage.removeItem(PROBE_KEY);
      } catch {
        /* Opt-in also works without storage. */
      }
      if (!until) {
        try {
          sessionStorage.removeItem(PROBE_HANDOFF_KEY);
        } catch {
          /* Expired diagnostics remain inactive when storage is unavailable. */
        }
        return;
      }
      const current = generation;
      void import('./navigation-probe')
        .then(({ startNavigationProbe: start }) => {
          if (disposed || current !== generation || Date.now() >= until || !output.current) return;
          probe.current = start(output.current);
          expiryTimer = setTimeout(stop, Math.max(0, until - Date.now()));
        })
        .catch(() => {
          /* Diagnostics cannot break the storefront. */
        });
    };
    sync();
    window.addEventListener('hashchange', sync);
    return () => {
      disposed = true;
      stop();
      window.removeEventListener('hashchange', sync);
    };
  }, []);
  useEffect(() => {
    probe.current?.routeCommitted();
  }, [pathname]);
  return <output hidden ref={output} />;
}
